import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export const firebaseConfigured = Object.values(firebaseConfig).every(Boolean);

const app = firebaseConfigured ? initializeApp(firebaseConfig) : null;

export const auth = app ? getAuth(app) : null;
export const db = app ? getFirestore(app) : null;
const MEDIA_UPLOAD_URL = process.env.REACT_APP_MEDIA_UPLOAD_URL || '';
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
const MAX_VIDEO_BYTES = 200 * 1024 * 1024;
const MAX_PDF_BYTES = 100 * 1024 * 1024;
const IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif']);
const VIDEO_MIME_TYPES = new Set(['video/mp4', 'video/webm', 'video/quicktime', 'video/x-m4v']);
const PDF_MIME_TYPES = new Set(['application/pdf', 'application/x-pdf']);

const prettyBytes = (bytes) => `${Math.round(bytes / (1024 * 1024))} MB`;

const validateMediaFile = (file, mediaType) => {
  if (!file) throw new Error('Please choose a file to upload');

  if (mediaType === 'pdf') {
    if (!PDF_MIME_TYPES.has(file.type) && !file.name?.toLowerCase().endsWith('.pdf')) {
      throw new Error('Please choose a valid PDF document');
    }
    if (file.size > MAX_PDF_BYTES) {
      throw new Error(`PDF must be under ${prettyBytes(MAX_PDF_BYTES)}`);
    }
    return;
  }

  if (mediaType === 'video') {
    if (!VIDEO_MIME_TYPES.has(file.type)) {
      throw new Error('Please choose an MP4, WEBM, or MOV video file');
    }
    if (file.size > MAX_VIDEO_BYTES) {
      throw new Error(`Video must be under ${prettyBytes(MAX_VIDEO_BYTES)}`);
    }
    return;
  }

  if (!IMAGE_MIME_TYPES.has(file.type)) {
    throw new Error('Please choose a JPG, PNG, WEBP, GIF, or AVIF image');
  }
  if (file.size > MAX_IMAGE_BYTES) {
    throw new Error(`Image must be under ${prettyBytes(MAX_IMAGE_BYTES)}`);
  }
};

const uploadToHostinger = async (file, folder, mediaType) => {
  const uploadUrl = MEDIA_UPLOAD_URL;
  if (!uploadUrl) return null;

  // Firebase ID tokens expire. Ask the SDK for a current token instead of
  // relying only on the copy saved when the user originally signed in.
  let token = localStorage.getItem('dp_token');
  if (auth?.currentUser) {
    token = await auth.currentUser.getIdToken();
    localStorage.setItem('dp_token', token);
  }
  if (!token) throw new Error('Please login again before uploading media');

  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);
  formData.append('mediaType', mediaType);

  const controller = new AbortController();
  const timeoutMs = mediaType === 'video' ? 600000 : mediaType === 'pdf' ? 300000 : 120000;
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
      signal: controller.signal,
    });

    const text = await response.text();
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch (e) {
      if (/^\s*</.test(text)) {
        throw new Error('Media upload endpoint returned the website HTML instead of JSON. Make sure media-upload.php is deployed at REACT_APP_MEDIA_UPLOAD_URL and is not being handled by the React route fallback.');
      }
      throw new Error(`Upload returned invalid response: ${text.slice(0, 160)}`);
    }

    if (!response.ok) {
      const error = new Error(data.error || `Hostinger upload failed (HTTP ${response.status})`);
      error.status = response.status;
      throw error;
    }
    if (!data.url) throw new Error('Upload completed but no media URL was returned');
    return data.url;
  } catch (e) {
    if (e.name === 'AbortError') {
      const label = mediaType === 'video' ? 'Video' : mediaType === 'pdf' ? 'PDF' : 'Image';
      throw new Error(`${label} upload timed out. Check your connection and Hostinger PHP upload limits, then try again.`);
    }
    if (e instanceof TypeError) {
      throw new Error('Cannot reach the media upload server. Check the media subdomain, SSL, and CORS configuration.');
    }
    throw e;
  } finally {
    window.clearTimeout(timeout);
  }
};

export const uploadMediaFile = async (file, folder = 'content', mediaType = 'image') => {
  validateMediaFile(file, mediaType);

  const hostingerUrl = await uploadToHostinger(file, folder, mediaType);
  if (hostingerUrl) return hostingerUrl;

  throw new Error('Media uploads require REACT_APP_MEDIA_UPLOAD_URL. In Vercel, set it to the Hostinger PHP endpoint URL, then redeploy the frontend.');
};

export default app;
